import { useMemo, useRef, useState } from 'react'
import styles from './ProducerSelectModal.module.scss'
import { useTranslation } from 'next-i18next/pages'
import { useGetLanguage } from 'hooks/useGetLanguage'
import NewProducerMap from 'components/producers/new-producer-map/NewProducerMap'
import { NewProducerMapProps } from 'components/producers/new-producer-map/types'
import { filterLocations } from 'features/producer-detail/utils'
import { Checkbox, Heading, Img, Modal } from '@boltenergy-be/design-system'
import { ProducerSelectModalHeaderProps, ProducerSelectModalProps } from './types'

const ProducerSelectModalHeader = ({ avatar, name }: ProducerSelectModalHeaderProps) => {
  // i18n
  const { t } = useTranslation('producer')

  return (
    <div className={styles.producer}>
      <Img className={styles.avatar} alt={name} publicId={avatar} width={60} height={60} />
      <small className={styles.info}>{t('producer', 'Energie-opwekker')}</small>
      <Heading as="h2" variant="h3" serif className={styles.title}>
        {name}
      </Heading>
    </div>
  )
}

const ProducerSelectModal = ({ producer, populatedLocations, ...modalProps }: ProducerSelectModalProps) => {
  // i18n
  const { t } = useTranslation('producer')
  const language = useGetLanguage()

  // Local state
  const [hideSoldOutLocations, setHideSoldOutLocations] = useState<boolean>(true)

  // Ref
  const mapRef = useRef<HTMLDivElement>(null)

  // Constants
  const filteredLocations = useMemo<NewProducerMapProps['locations']>(
    () => filterLocations(populatedLocations, hideSoldOutLocations),
    [hideSoldOutLocations, populatedLocations]
  )

  if (modalProps.isOpen) {
    return (
      <Modal
        {...modalProps}
        className={styles.modal}
        title={<ProducerSelectModalHeader avatar={producer.images.profile?.publicId ?? ''} name={producer.name[language]} />}
      >
        <Heading as="h2" variant="h3" className="mb-300">
          {t('modal.title', 'Kies een locatie om verder te gaan')}
        </Heading>
        <p>
          {t(
            'modal.description',
            'Deze energie-opwekker wekt stroom op op verschillende locaties. Kies een installatie in jouw buurt of één naar keuze.'
          )}
        </p>
        <form className={styles['toggle-wrapper']}>
          <Checkbox
            id="hide-sold-out"
            name="hideSoldOut"
            checked={hideSoldOutLocations}
            onChange={() => setHideSoldOutLocations(!hideSoldOutLocations)}
            label={t('map.hideSoldOut')}
          />
        </form>

        <div ref={mapRef}>
          <NewProducerMap
            locations={filteredLocations}
            height="470px"
            zoom={11}
            center={
              producer && {
                lat: Number((filteredLocations.length > 0 ? filteredLocations : populatedLocations)[0]?.coordinates?.lat),
                lng: Number((filteredLocations.length > 0 ? filteredLocations : populatedLocations)[0]?.coordinates?.long)
              }
            }
            enablePanning
            defaultOpenSlug={filteredLocations[0]?.slug}
          />
        </div>
      </Modal>
    )
  }

  return null
}

export default ProducerSelectModal
